/**
 * GLSL port of the 14 Immersiom formations from `engine/Formations.ts`.
 *
 * Same math, same audio uniforms, same coordinate conventions. Used by the
 * WebGL2 GPGPU update pass so the two engines render visually-identical
 * formations from the same audio frame.
 *
 * Conventions:
 *   - `rest` is a unit-sphere Fibonacci point (with the same hash jitter as
 *     the WebGPU init kernel).
 *   - `t` is time in seconds.
 *   - `baseR` is the shell base radius (defaults to 8.0).
 *   - `uBass`, `uMid`, `uTreble`, `uBeat` are 0..1 audio uniforms.
 */
export const FORMATIONS_GLSL = /* glsl */ `
vec3 dispatchFormation(
  int id,
  vec3 rest,
  float t,
  float baseR,
  float uBass,
  float uMid,
  float uTreble,
  float uBeat
) {
  vec2 rxz = rest.xz;
  float rho2D = length(rxz) + 0.001;
  float phi = atan(rest.z, rest.x);
  float sinTheta2 = max(0.0, 1.0 - rest.y * rest.y);

  if (id == 0) {
    // SUPERFORM — integer petal modes blended in time (no phi seam).
    float blend = sin(t * 0.18) * 0.5 + 0.5;
    float wave5 = cos(phi * 5.0);
    float wave8 = cos(phi * 8.0);
    float petal = mix(wave5, wave8, blend);
    float amp = 0.14 + uBass * 0.18 + uBeat * 0.20;
    float breath = 1.0 + sin(t * 0.45) * 0.06;
    float modulator = breath + petal * amp;
    return rest * baseR * modulator;
  }

  if (id == 1) {
    // HOPF FIBRATION — double rotation, no stereographic singularity.
    float tw1 = rest.y * 2.8 + t * 0.35 + uBass * 0.6;
    float tw2 = rest.x * 2.4 + t * 0.22 + uMid * 0.5;
    float c1 = cos(tw1); float s1 = sin(tw1);
    float ax = rest.x * c1 - rest.z * s1;
    float az = rest.x * s1 + rest.z * c1;
    float c2 = cos(tw2); float s2 = sin(tw2);
    float by = rest.y * c2 - az * s2;
    float bz = rest.y * s2 + az * c2;
    return vec3(ax, by, bz) * baseR;
  }

  if (id == 2) {
    // RADIAL PULSE
    float freq = 5.0 + uMid * 2.5;
    float amp = 0.12 + uBass * 0.25 + uBeat * 0.18;
    float modulator = 1.0 + amp * sin(rest.y * freq - t * 1.6);
    return rest * baseR * modulator;
  }

  if (id == 3) {
    // SINE SURGE
    float spread = baseR * 1.4;
    float px = rest.x * spread;
    float pz = rest.z * spread;
    float yAmp = 1.4 + uMid * 0.7 + uBass * 0.4;
    float wave = sin(rho2D * 2.4 - t * 1.3) * yAmp
      + sin(rest.x * 1.7 + t * 0.8) * yAmp * 0.35;
    float py = rest.y * baseR * 0.25 + wave;
    return vec3(px, py, pz);
  }

  if (id == 4) {
    // STELLARATOR — helical-flux modulated sphere (no torus pinch).
    float helix = cos(rest.y * 5.0 + phi * 3.0 + t * 0.4);
    float amp = 0.17 + uBass * 0.22 + uBeat * 0.18;
    float modulator = 1.0 + helix * amp;
    return rest * baseR * modulator;
  }

  if (id == 5) {
    // ACCRETION DISK
    float kepler = 0.6 / sqrt(rho2D + 0.4);
    float angle = phi + kepler * t;
    float r = rho2D * baseR * 1.5;
    float dx = r * cos(angle);
    float dz = r * sin(angle);
    float ripple = sin(rho2D * 8.0 - t * 2.0) * (0.25 + uMid * 0.25);
    float dy = rest.y * baseR * 0.08 + ripple;
    return vec3(dx, dy, dz);
  }

  if (id == 6) {
    // PLASMA SHEET
    float spread = baseR * 1.5;
    float px = rest.x * spread;
    float pz = rest.z * spread;
    float amp = 0.55 + uMid * 0.55 + uTreble * 0.2;
    float w1 = sin(rest.x * 2.5 - t * 1.5) * 1.0;
    float w2 = sin(rest.z * 2.0 + t * 1.2) * 0.7;
    float w3 = sin((rest.x + rest.z) * 1.7 - t * 0.9) * 0.5;
    float py = (w1 + w2 + w3) * amp * baseR * 0.18 + rest.y * baseR * 0.18;
    return vec3(px, py, pz);
  }

  if (id == 7) {
    // NOISE BLOOM
    float amp = 0.18 + uMid * 0.32 + uTreble * 0.15;
    float n1 = sin(rest.x * 4.0 + t * 0.7);
    float n2 = sin(rest.y * 3.0 + t * 1.1 + 1.3);
    float n3 = sin(rest.z * 5.0 + t * 0.9 + 2.4);
    float bloom = 1.0 + amp * n1 * n2 * n3;
    return rest * baseR * bloom;
  }

  if (id == 8) {
    // HARMONIC SHELL — integer azimuthal modes (no phi seam).
    float blend = sin(t * 0.15) * 0.5 + 0.5;
    float h2 = cos(phi * 2.0);
    float h3 = cos(phi * 3.0);
    float harmonic = sinTheta2 * mix(h2, h3, blend);
    float amp = 0.22 + uMid * 0.25 + uBeat * 0.20;
    float modulator = 1.0 + harmonic * amp;
    return rest * baseR * modulator;
  }

  if (id == 9) {
    // STRANGE ATTRACTOR
    vec3 p = rest * 3.5;
    float swirl = 1.4 + sin(t * 0.25) * 0.3;
    float ox = sin(p.y + t * 0.15) - p.x * 0.18;
    float oy = sin(p.z + t * 0.20) - p.y * 0.18;
    float oz = sin(p.x + t * 0.10) - p.z * 0.18;
    vec3 offset = vec3(ox, oy, oz) * swirl;
    return (p + offset) * baseR * 0.35;
  }

  if (id == 10) {
    // LORENZ DRIFT — smooth lobe ramp through origin.
    float softLobe = sin(rest.x * 1.5707963);
    vec3 p = rest * baseR * 0.7;
    float driftX = sin(p.y * 0.4 + t * 0.3) * baseR * 0.32 * softLobe;
    float driftY = sin(p.z * 0.5 + t * 0.5) * baseR * 0.22;
    float driftZ = sin(p.x * 0.45 - t * 0.4) * baseR * 0.30;
    return p + vec3(driftX, driftY, driftZ);
  }

  if (id == 11) {
    // ORGANIC BREATH
    float breath = 1.0 + sin(t * 0.55) * (0.14 + uBass * 0.22);
    float lobe = cos(rest.x * 1.8) * cos(rest.y * 2.2) * cos(rest.z * 1.4);
    float lobeAmp = 0.12 + uMid * 0.18;
    float modulator = breath + lobe * lobeAmp * sin(t * 0.35);
    return rest * baseR * modulator;
  }

  if (id == 12) {
    // SPIRAL VORTEX — y-twist + radial pulse on the sphere.
    float twist = rest.y * 3.2 + t * 0.5 + uBass * 1.3;
    float c = cos(twist); float s = sin(twist);
    float px = rest.x * c - rest.z * s;
    float pz = rest.x * s + rest.z * c;
    float pulse = 1.0 + sin(rho2D * 4.5 - t * 1.8) * (0.14 + uMid * 0.10);
    return vec3(px, rest.y, pz) * baseR * pulse;
  }

  // HELICAL TWIST (default / id == 13)
  float twistAmount = 1.6 + uTreble * 1.4 + uMid * 0.5;
  float twistAngle = rest.y * twistAmount + t * 0.25;
  float ca = cos(twistAngle); float sa = sin(twistAngle);
  vec3 p = rest * baseR;
  float tx = p.x * ca - p.z * sa;
  float ty = p.y;
  float tz = p.x * sa + p.z * ca;
  return vec3(tx, ty, tz);
}

vec3 fibonacciRest(float i, float invCount) {
  float ti = i * invCount;
  float y = 1.0 - ti * 2.0;
  float r = sqrt(max(0.0, 1.0 - y * y));
  float phi = i * 2.39996322972865332;
  float x = r * cos(phi);
  float z = r * sin(phi);
  // Same deterministic hash jitter as the WebGPU init kernel.
  float h1 = fract(sin(i * 12.9898 + 78.233) * 43758.5453) - 0.5;
  float h2 = fract(sin(i * 39.346 + 11.135) * 43758.5453) - 0.5;
  float h3 = fract(sin(i * 83.412 + 34.567) * 43758.5453) - 0.5;
  vec3 jitter = vec3(h1, h2, h3) * 0.012;
  return normalize(vec3(x, y, z) + jitter);
}
`;
